import { Injectable } from '@angular/core';
import { CVApiService } from 'src/app/reusables/services/cv-api.service';
import { UserProfileDTO } from '../admin/user/user-admin.model';
import {
    WallboardsApiModel, Wallboard, WallboardSlide, WallboardModuleDetails, FullImageDetailsApiModel, TypeStateApiModel, SlideControlApiModel, LoginListObj, RacingSetupTransferObj, GuageSettingsTransferObj,
    ThermometerSettingsTransferObj, TextSettingsTransferObj, ImageSettingsTransferObj, TopSettingsTransferObj, KpiSettingsTransferObj, SummarySettingsTransferObj, VideoSettingsTransferObj, LeaderboardSetupObj,
    SpotlightSetupObj, CurrentAgentStatesSettingTransferObj, WallboardGameSetupObj, ChallengesSetupObj, ActiveChallengesWsObj, GamesLandingLoadWsObj, MarketplaceSetupObj, CounterSettingsTransferObj,
    ModuleListWrapperObj, ModuleType
} from './wallboards.model';

@Injectable({
    providedIn: 'root'
})
export class WallboardsService {
    constructor(
        private cvApiService: CVApiService
    ) { }

    public async loadWallboards(): Promise<WallboardsApiModel> {
        try {
            let response = await this.cvApiService.get<WallboardsApiModel>(`/api/v1/wallboards`);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to load wallboards.`);
        }
    }

    public async saveWallboard(wallboard: Wallboard): Promise<Wallboard> {
        try {
            let response = await this.cvApiService.post<Wallboard>(`/api/v1/wallboard`, JSON.stringify(wallboard));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to save wallboard ${wallboard.WallboardID}.`);
        }
    }

    public async updateWallboardAssignments(wallboardId, dataList): Promise<boolean> {
        try {
            let response = await this.cvApiService.post<boolean>(`/api/v2/updateWallboardAssignments/${wallboardId}`, JSON.stringify(dataList));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to update assignments for wallboard ${wallboardId}.`);
        }
    }

    public async copyWallboard(wallboard: Wallboard, oldCreatorId: number): Promise<Wallboard> {
        try {
            let response = await this.cvApiService.post<Wallboard>(`/api/v1/wallboardCopy/${oldCreatorId}`, JSON.stringify(wallboard));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to copy wallboard ${wallboard.WallboardID}.`);
        }
    }

    public async deleteWallboard(wallboard: Wallboard): Promise<void> {
        try {
            let response = await this.cvApiService.put<void>(`/api/v1/wallboard`, JSON.stringify(wallboard));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to delete wallboard ${wallboard.WallboardID}.`);
        }
    }

    public async loadWallboardSlides(wallboardId: number, slideIndex: number = -1): Promise<WallboardSlide[]> {
        try {
            let response = await this.cvApiService.get<WallboardSlide[]>(`/api/v1/wallboardInfo/${wallboardId}/${slideIndex}`);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to load wallboard info for ${wallboardId}.`);
        }
    }

    public async saveLeaderboardDetails(slide: WallboardSlide, leaderboardObj: LeaderboardSetupObj, creatorId: number, displayName: string): Promise<WallboardSlide> {
        try {
            let response = await this.cvApiService.post<WallboardSlide>(`/api/v1/leaderboardDetails`, JSON.stringify(
                {
                    Led_Obj: leaderboardObj,
                    Slide: slide,
                    Metric_Display_Name: displayName,
                    Creator_Id: creatorId,
                    Module_Key: slide.WallboardModuleDetails[0].WallboardModuleKey
                }
            ));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to save leaderboard details for ${slide.WallboardSlideID}.`);
        }
    }

    public async saveLeaderboardState(slide: WallboardSlide, leaderboardObj: LeaderboardSetupObj, creatorId: number, displayName: string): Promise<void> {
        try {
            let response = await this.cvApiService.put<void>(`/api/v1/leaderboardState`, JSON.stringify(
                {
                    Led_Obj: leaderboardObj,
                    Slide: slide,
                    Metric_Display_Name: displayName,
                    Creator_Id: creatorId,
                    Module_Key: slide.WallboardModuleDetails[0].WallboardModuleKey
                }
            ));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to save leaderboard state for ${slide.WallboardSlideID}.`);
        }
    }

    public async saveRacingDetails(slide: WallboardSlide, racingObj: RacingSetupTransferObj, creatorId: number, displayName: string): Promise<WallboardSlide> {
        try {
            let response = await this.cvApiService.post<WallboardSlide>(`/api/v1/racingDetails`, JSON.stringify(
                {
                    Updated_Slide: slide,
                    Setup_Obj: racingObj,
                    Slide_Key: slide.WallboardModuleDetails[0].WallboardModuleKey,
                    Metric_Display_Name: displayName,
                    Creator_Id: creatorId
                }
            ));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to save racing details for ${slide.WallboardSlideID}.`);
        }
    }

    public async saveFullSummaryDetails(slide: WallboardSlide, summaryObj: SummarySettingsTransferObj, creatorId: number, displayNames: {}[]): Promise<WallboardSlide> {
        try {
            let response = await this.cvApiService.post<WallboardSlide>(`/api/v1/fullSummaryDetails`, JSON.stringify(
                {
                    Sum_Obj: summaryObj,
                    Slide: slide,
                    Metric_Display_Names: displayNames,
                    Creator_Id: creatorId,
                    Module_Key: slide.WallboardModuleDetails[0].WallboardModuleKey
                }
            ));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to save summary details for ${slide.WallboardSlideID}.`);
        }
    }

    public async saveFullSummaryState(slide: WallboardSlide, summaryObj: SummarySettingsTransferObj, creatorId: number, displayNames: {}[]): Promise<void> {
        try {
            let response = await this.cvApiService.put<void>(`/api/v1/fullSummaryState`, JSON.stringify(
                {
                    Sum_Obj: summaryObj,
                    Slide: slide,
                    Metric_Display_Names: displayNames,
                    Creator_Id: creatorId,
                    Module_Key: slide.WallboardModuleDetails[0].WallboardModuleKey
                }
            ));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to save summary state for ${slide.WallboardSlideID}.`);
        }
    }

    public async saveFullImageDetails(slide: WallboardSlide, imgString: string, creatorId: number): Promise<FullImageDetailsApiModel> {
        try {
            let response = await this.cvApiService.post<FullImageDetailsApiModel>(`/api/v1/fullImageDetails`, JSON.stringify(
                {
                    Slide: slide,
                    Img_Str: imgString,
                    Creator_Id: creatorId,
                    Module_Detail: slide.WallboardModuleDetails[0]
                }
            ));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to save image details for ${slide.WallboardSlideID}.`);
        }
    }

    public async saveFullImageState(slide: WallboardSlide, imgString: string, creatorId: number): Promise<number> {
        try {
            let response = await this.cvApiService.put<number>(`/api/v1/fullImageStateDetails`, JSON.stringify(
                {
                    Slide: slide,
                    Img_Str: imgString,
                    Creator_Id: creatorId,
                    Module_Detail: slide.WallboardModuleDetails[0]
                }
            ));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to save image state for ${slide.WallboardSlideID}.`);
        }
    }

    public async saveFullVideoDetails(slide: WallboardSlide, videoObj: VideoSettingsTransferObj, creatorId: number): Promise<WallboardSlide> {
        try {
            let response = await this.cvApiService.post<WallboardSlide>(`/api/v1/fullVideoDetails`, JSON.stringify(
                {
                    Vid_Obj: videoObj,
                    Slide: slide,
                    Creator_Id: creatorId,
                    Module_Key: slide.WallboardModuleDetails[0].WallboardModuleKey
                }
            ));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to save video details for ${slide.WallboardSlideID}.`);
        }
    }

    public async saveFullVideoState(slide: WallboardSlide, videoObj: VideoSettingsTransferObj, creatorId: number): Promise<void> {
        try {
            let response = await this.cvApiService.put<void>(`/api/v1/fullVideoState`, JSON.stringify(
                {
                    Vid_Obj: videoObj,
                    Slide: slide,
                    Creator_Id: creatorId,
                    Module_Key: slide.WallboardModuleDetails[0].WallboardModuleKey
                }
            ));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to save video state for ${slide.WallboardSlideID}.`);
        }
    }

    public async saveSpotlightDetails(slide: WallboardSlide, spotlightSetupObj: SpotlightSetupObj, creatorId: number): Promise<WallboardSlide> {
        try {
            let response = await this.cvApiService.post<WallboardSlide>(`/api/v1/spotlightDetails`, JSON.stringify(
                {
                    Spot_Obj: spotlightSetupObj,
                    Slide: slide,
                    Creator_Id: creatorId,
                    Module_Key: slide.WallboardModuleDetails[0].WallboardModuleKey
                }
            ));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to save spotlight details for ${slide.WallboardSlideID}.`);
        }
    }

    public async saveSpotlightState(slide: WallboardSlide, spotlightSetupObj: SpotlightSetupObj, creatorId: number): Promise<void> {
        try {
            let response = await this.cvApiService.put<void>(`/api/v1/spotlightState`, JSON.stringify(
                {
                    Spot_Obj: spotlightSetupObj,
                    Slide: slide,
                    Creator_Id: creatorId,
                    Module_Key: slide.WallboardModuleDetails[0].WallboardModuleKey
                }
            ));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to save spotlight state for ${slide.WallboardSlideID}.`);
        }
    }

    public async saveCurrentAgentStatesDetails(slide: WallboardSlide, currentAgentStatesObj: CurrentAgentStatesSettingTransferObj, creatorId: number): Promise<WallboardSlide> {
        try {
            let response = await this.cvApiService.post<WallboardSlide>(`/api/v1/currentAgentStatesDetails`, JSON.stringify(
                {
                    Cas_Obj: currentAgentStatesObj,
                    Slide: slide,
                    Creator_Id: creatorId,
                    Module_Key: slide.WallboardModuleDetails[0].WallboardModuleKey
                }
            ));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to save current agent states details for ${slide.WallboardSlideID}.`);
        }
    }

    public async saveResponsiveCurrentAgentStatesDetails(slide: WallboardSlide, currentAgentStatesObj: CurrentAgentStatesSettingTransferObj, creatorId: number): Promise<WallboardSlide> {
        try {
            let response = await this.cvApiService.post<WallboardSlide>(`/api/v1/responsiveCurrentAgentStatesDetails`, JSON.stringify(
                {
                    Cas_Obj: currentAgentStatesObj,
                    Slide: slide,
                    Creator_Id: creatorId,
                    Module_Key: slide.WallboardModuleDetails[0].WallboardModuleKey
                }
            ));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to save responsive current agent states details for ${slide.WallboardSlideID}.`);
        }
    }

    public async saveCurrentAgentStatesState(slide: WallboardSlide, currentAgentStatesObj: CurrentAgentStatesSettingTransferObj, creatorId: number): Promise<void> {
        try {
            let response = await this.cvApiService.put<void>(`/api/v1/currentAgentStatesState`, JSON.stringify(
                {
                    Cas_Obj: currentAgentStatesObj,
                    Slide: slide,
                    Creator_Id: creatorId,
                    Module_Key: slide.WallboardModuleDetails[0].WallboardModuleKey
                }
            ));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to save current agent states state for ${slide.WallboardSlideID}.`);
        }
    }

    public async saveWallboardGameDetails(slide: WallboardSlide, wallboardGameSetupObj: WallboardGameSetupObj, creatorId: number): Promise<WallboardSlide> {
        try {
            let response = await this.cvApiService.post<WallboardSlide>(`/api/v1/wallboardGameDetails`, JSON.stringify(
                {
                    Game_Obj: wallboardGameSetupObj,
                    Slide: slide,
                    Slide_Key: slide.WallboardModuleDetails[0].WallboardModuleKey,
                    Creator_Id: creatorId
                }
            ));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to save wallboard game details for ${slide.WallboardSlideID}.`);
        }
    }

    public async saveWallboardGameState(slide: WallboardSlide, wallboardGameSetupObj: WallboardGameSetupObj, creatorId: number): Promise<void> {
        try {
            let response = await this.cvApiService.put<void>(`/api/v1/wallboardGameState`, JSON.stringify(
                {
                    Game_Obj: wallboardGameSetupObj,
                    Slide: slide,
                    Creator_Id: creatorId
                }
            ));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to save game wallboard state for ${slide.WallboardSlideID}.`);
        }
    }

    public async saveChallengesDetails(slide: WallboardSlide, challengesObj: ChallengesSetupObj, creatorId: number): Promise<WallboardSlide> {
        try {
            let response = await this.cvApiService.post<WallboardSlide>(`/api/v1/challengesDetails`, JSON.stringify(
                {
                    Cst_Obj: challengesObj,
                    Slide: slide,
                    Creator_Id: creatorId,
                    Module_Key: slide.WallboardModuleDetails[0].WallboardModuleKey
                }
            ));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to save challenge details for ${slide.WallboardSlideID}.`);
        }
    }

    public async saveChallengesState(slide: WallboardSlide, challengesObj: ChallengesSetupObj, creatorId: number): Promise<void> {
        try {
            let response = await this.cvApiService.put<void>(`/api/v1/challengesState`, JSON.stringify(
                {
                    Cst_Obj: challengesObj,
                    Slide: slide,
                    Creator_Id: creatorId,
                    Module_Key: slide.WallboardModuleDetails[0].WallboardModuleKey
                }
            ));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to save challenge state for ${slide.WallboardSlideID}.`);
        }
    }

    public async saveMarketplaceDetails(slide: WallboardSlide, marketplaceObj: MarketplaceSetupObj, creatorId: number): Promise<WallboardSlide> {
        try {
            let response = await this.cvApiService.post<WallboardSlide>(`/api/v1/marketplaceDetails`, JSON.stringify(
                {
                    Mst_Obj: marketplaceObj,
                    Slide: slide,
                    Creator_Id: creatorId,
                    Module_Key: slide.WallboardModuleDetails[0].WallboardModuleKey
                }
            ));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to save marketplace details for ${slide.WallboardSlideID}.`);
        }
    }

    public async saveMarketplaceState(slide: WallboardSlide, marketplaceObj: MarketplaceSetupObj, creatorId: number): Promise<void> {
        try {
            let response = await this.cvApiService.put<void>(`/api/v1/marketplaceState`, JSON.stringify(
                {
                    Mst_Obj: marketplaceObj,
                    Slide: slide,
                    Creator_Id: creatorId,
                    Module_Key: slide.WallboardModuleDetails[0].WallboardModuleKey
                }
            ));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to save marketplace state for ${slide.WallboardSlideID}.`);
        }
    }

    public async saveGaugeDetails(gaugeObj: GuageSettingsTransferObj, customDetails: WallboardModuleDetails, creatorId: number, displayName: string): Promise<WallboardModuleDetails> {
        try {
            let response = await this.cvApiService.post<WallboardModuleDetails>(`/api/v1/gaugeDetails`, JSON.stringify(
                {
                    Gauge_Obj: gaugeObj,
                    Cur_Details: customDetails,
                    Metric_Display_Name: displayName,
                    Creator_Id: creatorId
                }
            ));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to save gauge details for ${customDetails.WallboardModuleDetailsID}.`);
        }
    }

    public async saveGaugeState(gaugeObj: GuageSettingsTransferObj, key: string, creatorId: number, displayName: string): Promise<void> {
        try {
            let response = await this.cvApiService.put<void>(`/api/v1/gaugeState`, JSON.stringify(
                {
                    Gauge_Obj: gaugeObj,
                    Key: key,
                    Metric_Display_Name: displayName,
                    Creator_Id: creatorId
                }
            ));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to save gauge state for ${key}.`);
        }
    }

    public async saveThermometerDetails(thermObj: ThermometerSettingsTransferObj, customDetails: WallboardModuleDetails, creatorId: number, displayName: string): Promise<WallboardModuleDetails> {
        try {
            let response = await this.cvApiService.post<WallboardModuleDetails>(`/api/v1/thermometerDetails`, JSON.stringify(
                {
                    Therm_Obj: thermObj,
                    Cur_Details: customDetails,
                    Metric_Display_Name: displayName,
                    Creator_Id: creatorId
                }
            ));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to save thermometer details for ${customDetails.WallboardModuleDetailsID}.`);
        }
    }

    public async saveThermometerState(thermObj: ThermometerSettingsTransferObj, key: string, creatorId: number, displayName: string): Promise<void> {
        try {
            let response = await this.cvApiService.put<void>(`/api/v1/thermometerState`, JSON.stringify(
                {
                    Therm_Obj: thermObj,
                    Key: key,
                    Metric_Display_Name: displayName,
                    Creator_Id: creatorId
                }
            ));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to save thermometer state for ${key}.`);
        }
    }

    public async saveSummaryDetails(sumObj: SummarySettingsTransferObj, customDetails: WallboardModuleDetails, creatorId: number, displayNames: string[]): Promise<WallboardModuleDetails> {
        try {
            let response = await this.cvApiService.post<WallboardModuleDetails>(`/api/v1/summaryDetails`, JSON.stringify(
                {
                    Sum_Obj: sumObj,
                    Cur_Details: customDetails,
                    Metric_Display_Names: displayNames,
                    Creator_Id: creatorId
                }
            ));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to save summary details for ${customDetails.WallboardModuleDetailsID}.`);
        }
    }

    public async saveSummaryState(sumObj: SummarySettingsTransferObj, key: string, creatorId: number, displayNames: string[]): Promise<void> {
        try {
            let response = await this.cvApiService.put<void>(`/api/v1/summaryState`, JSON.stringify(
                {
                    Sum_Obj: sumObj,
                    Key: key,
                    Metric_Display_Names: displayNames,
                    Creator_Id: creatorId
                }
            ));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to save summary state for ${key}.`);
        }
    }

    public async saveTextDetails(textObj: TextSettingsTransferObj, customDetails: WallboardModuleDetails, creatorId: number): Promise<WallboardModuleDetails> {
        try {
            let response = await this.cvApiService.post<WallboardModuleDetails>(`/api/v1/textDetails`, JSON.stringify(
                {
                    Text_Obj: textObj,
                    Cur_Details: customDetails,
                    Creator_Id: creatorId
                }
            ));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to save text details for ${customDetails.WallboardModuleDetailsID}.`);
        }
    }

    public async saveTextState(textObj: TextSettingsTransferObj, key: string, creatorId: number): Promise<void> {
        try {
            let response = await this.cvApiService.put<void>(`/api/v1/textState`, JSON.stringify(
                {
                    Text_Obj: textObj,
                    Key: key,
                    Creator_Id: creatorId
                }
            ));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to save text state for ${key}.`);
        }
    }

    public async saveTopDetails(topObj: TopSettingsTransferObj, customDetails: WallboardModuleDetails, creatorId: number, displayName: string): Promise<WallboardModuleDetails> {
        try {
            let response = await this.cvApiService.post<WallboardModuleDetails>(`/api/v1/topDetails`, JSON.stringify(
                {
                    Top_Obj: topObj,
                    Cur_Details: customDetails,
                    Metric_Display_Name: displayName,
                    Creator_Id: creatorId
                }
            ));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to save top details for ${customDetails.WallboardModuleDetailsID}.`);
        }
    }

    public async saveTopState(topObj: TopSettingsTransferObj, key: string, creatorId: number, displayName: string): Promise<void> {
        try {
            let response = await this.cvApiService.put<void>(`/api/v1/topState`, JSON.stringify(
                {
                    Top_Obj: topObj,
                    Key: key,
                    Metric_Display_Name: displayName,
                    Creator_Id: creatorId
                }
            ));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to save top state for ${key}.`);
        }
    }

    public async saveCounterDetails(counterObj: CounterSettingsTransferObj, customDetails: WallboardModuleDetails, creatorId: number): Promise<WallboardModuleDetails> {
        try {
            let response = await this.cvApiService.post<WallboardModuleDetails>(`/api/v1/counterDetails`, JSON.stringify(
                {
                    Counter_Obj: counterObj,
                    Cur_Details: customDetails,
                    Creator_Id: creatorId
                }
            ));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to save counter details for ${customDetails.WallboardModuleDetailsID}.`);
        }
    }

    public async saveCounterState(counterObj: CounterSettingsTransferObj, key: string, creatorId: number): Promise<void> {
        try {
            let response = await this.cvApiService.put<void>(`/api/v1/counterState`, JSON.stringify(
                {
                    Counter_Obj: counterObj,
                    Key: key,
                    Creator_Id: creatorId
                }
            ));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to save counter state for ${key}.`);
        }
    }

    public async saveKpiDetails(kpiObj: KpiSettingsTransferObj, customDetails: WallboardModuleDetails, creatorId: number, displayName: string): Promise<WallboardModuleDetails> {
        try {
            let response = await this.cvApiService.put<WallboardModuleDetails>(`/api/v1/kpiDetails`, JSON.stringify(
                {
                    Kpi_Obj: kpiObj,
                    Cur_Details: customDetails,
                    Metric_Display_Name: displayName,
                    Creator_Id: creatorId
                }
            ));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to save KPI details for ${customDetails.WallboardModuleDetailsID}.`);
        }
    }

    public async saveKpiState(kpiObj: KpiSettingsTransferObj, key: string, creatorId: number, displayName: string): Promise<void> {
        try {
            let response = await this.cvApiService.put<void>(`/api/v1/kpiState`, JSON.stringify(
                {
                    Kpi_Obj: kpiObj,
                    Key: key,
                    Metric_Display_Name: displayName,
                    Creator_Id: creatorId
                }
            ));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to save KPI state for ${key}.`);
        }
    }

    public async saveImageDetails(imageObj: ImageSettingsTransferObj, customDetails: WallboardModuleDetails, creatorId: number): Promise<any> {
        try {
            let response = await this.cvApiService.post<any>(`/api/v1/imageDetails`, JSON.stringify( // ImageDetailsApiModel
                {
                    Img_Obj: imageObj,
                    Cur_Details: customDetails,
                    Img_Str: imageObj.UploadedImageString,
                    Creator_Id: creatorId
                }
            ));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to save image details for ${customDetails.WallboardModuleDetailsID}.`);
        }
    }

    public async saveImageState(imageStr: string, key: string, creatorId: number): Promise<number> {
        try {
            let response = await this.cvApiService.put<number>(`/api/v1/imageState`, JSON.stringify(
                {
                    Img_Str: imageStr,
                    Key: key,
                    Creator_Id: creatorId
                }
            ));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to save image state for ${key}.`);
        }
    }

    public async updateSlide(slide: WallboardSlide): Promise<void> {
        try {
            let response = await this.cvApiService.put<void>(`/api/v1/slide`, JSON.stringify(slide));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to update info for slide ${slide.WallboardSlideID}.`);
        }
    }

    public async saveNewSlide(newSlide: WallboardSlide, slideType: ModuleType): Promise<WallboardSlide> {
        try {
            let response = await this.cvApiService.post<WallboardSlide>(`/api/v1/wallboardSlide/${slideType}`, JSON.stringify(newSlide));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to save new slide ${newSlide.SlideName}.`);
        }
    }

    public async saveSlideCopy(newSlide: WallboardSlide, detailsList: WallboardModuleDetails[], creatorId: number): Promise<WallboardSlide> {
        try {
            let response = await this.cvApiService.post<WallboardSlide>(`/api/v1/slideCopy`, JSON.stringify(
                {
                    New_Slide_Dto: newSlide,
                    Details_List: detailsList,
                    Creator_Id: creatorId
                }
            ));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to save copied slide ${newSlide.WallboardSlideID}.`);
        }
    }

    public async removeSlide(slide: WallboardSlide): Promise<void> {
        try {
            let response = await this.cvApiService.post<void>(`/api/v1/slide`, JSON.stringify(slide));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to remove slide ${slide.WallboardSlideID}.`);
        }
    }

    public async updateSlideEnabled(slideId: number, isEnabled: boolean): Promise<void> {
        try {
            let response = await this.cvApiService.put<void>(`/api/v1/slideEnabled/${slideId}/${isEnabled}`);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to update slide enabled status.`);
        }
    }

    public async updateSlideOrder(slides: WallboardSlide[]): Promise<void> {
        try {
            let response = await this.cvApiService.put<void>(`/api/v1/slideOrder`, JSON.stringify(slides));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to update slide order.`);
        }
    }

    public async updateSlideTheme(slideObj: RacingSetupTransferObj, slideKey: string, creatorId: number): Promise<void> {
        try {
            let response = await this.cvApiService.put<void>(`/api/v1/slideTheme/${slideKey}/${creatorId}`, JSON.stringify(slideObj));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to update racing theme for slide with key ${slideKey}.`);
        }
    }

    public async loadModuleStates(typeList: number[], keyList: string[], creatorId: number): Promise<ModuleListWrapperObj[]> {
        try {
            let response = await this.cvApiService.post<ModuleListWrapperObj[]>(`/api/v1/moduleStates`, JSON.stringify({
                Type_List: typeList,
                Key_List: keyList,
                Creator_Id: creatorId
            }));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to load module states.`);
        }
    }

    public async updateModulePosition(moduleDetails: WallboardModuleDetails): Promise<void> {
        try {
            let response = await this.cvApiService.put<void>(`/api/v1/modulePosition`, JSON.stringify(moduleDetails));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to update size/position of module ${moduleDetails.WallboardModuleDetailsID}.`);
        }
    }

    public async removeModule(moduleDetails: WallboardModuleDetails): Promise<void> {
        try {
            let response = await this.cvApiService.put<void>(`/api/v1/detail`, JSON.stringify(moduleDetails));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to remove module ${moduleDetails.WallboardModuleDetailsID}.`);
        }
    }

    public async saveUploadedBackground(imageString: string | ArrayBuffer): Promise<number> {
        try {
            let response = await this.cvApiService.post<number>(`/api/v1/backgroundImage`, JSON.stringify(imageString));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to save uploaded slide background.`);
        }
    }

    public async deactivateSlideBackground(backgroundId: number): Promise<void> {
        try {
            let response = await this.cvApiService.put<void>(`/api/v1/deactivateBackgroundImage/${backgroundId}`);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to deactivate background ${backgroundId}.`);
        }
    }

    public async loadTypeState(slideTypeId: number, slideKey: string, creatorId: number): Promise<TypeStateApiModel> {
        try {
            let response = await this.cvApiService.post<TypeStateApiModel>(`/api/v1/typeState/${slideTypeId}/${creatorId}`, JSON.stringify(slideKey));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to load type state for ${slideTypeId}, key ${slideKey}.`);
            // Some modules in view mode have countermeasures for failed loads, so this is helpful
            throw error;
        }
    }

    public async loadSlideControls(slideKey: string, creatorId: number, maxMetric: number, hasToggle: boolean): Promise<SlideControlApiModel> {
        try {
            let response = await this.cvApiService.post<SlideControlApiModel>(`/api/v1/slideControls/${creatorId}/${maxMetric}/${hasToggle}`, JSON.stringify(slideKey));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to load slide controls for ${slideKey}.`);
        }
    }

    public async loadCounterControls(slideKey: string, creatorId: number): Promise<any> {
        try {
            let response = await this.cvApiService.post<any>(`/api/v1/counterControls/${creatorId}`, JSON.stringify(slideKey)); //CounterControlApiModel
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to load counter controls for ${slideKey}.`);
        }
    }

    public async revertControlStates(creatorId: number, key: string): Promise<void> {
        try {
            let response = await this.cvApiService.put<void>(`/api/v1/revertControlStates/${creatorId}`, JSON.stringify(key));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to revert control states for ${key}.`);
        }
    }

    public async getImageAspect(imgId: number): Promise<number> {
        try {
            let response = await this.cvApiService.get<number>(`/api/v1/imageAspect/${imgId}`);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to load aspect ratio for ${imgId}.`);
            // Some modules in view mode have countermeasures for failed loads, so this is helpful
            throw error;
        }
    }

    public async loadBackgroundList(): Promise<number[]> {
        try {
            let response = await this.cvApiService.get<number[]>(`/api/v1/backgroundList`);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to load background list.`);
        }
    }

    // TODO: This just accomplishes the same thing that racingThemePreviewImageId does, except that, because it only pulls and stores the expanded information from each slide one at a time,
    // you can't get the theme so you have to ask the server to check the state for the slide to get the theme. Needless to say, this is pretty bad, and if racing slides just hold onto their own
    // background ID, it wouldn't be necessary.
    public async loadRacingBackgroundId(key: string, creatorId: number): Promise<number> {
        try {
            let response = await this.cvApiService.post<number>(`/api/v1/racingBackground/${creatorId}`, JSON.stringify(key));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to load racing background ID.`);
        }
    }

    public async loadLoginList(creatorId: number): Promise<LoginListObj[]> {
        try {
            let response = await this.cvApiService.get<LoginListObj[]>(`/api/v1/loginList/${creatorId}`);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to load login list.`);
        }
    }

    public async loadSpotlightLoginInfo(loginId: number): Promise<UserProfileDTO> {
        try {
            let response = await this.cvApiService.get<UserProfileDTO>(`/api/v1/spotlightLoginInfo/${loginId}`);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to load spotlight login info for ${loginId}.`);
        }
    }

    public async loadSpotlightBadges(): Promise<any[]> {
        try {
            let response = await this.cvApiService.get<any[]>(`/api/v1/spotlightBadges`); //Image[]
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to load spotlight badges.`);
        }
    }

    public async loadGames(gameId: number = null): Promise<GamesLandingLoadWsObj> {
        try {
            let response = await this.cvApiService.get<GamesLandingLoadWsObj>(`/api/v1/gamification/games${gameId != null ? "/" + gameId : ""}`);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to load games.`);
            // Some modules in view mode have countermeasures for failed loads, so this is helpful
            throw error;
        }
    }

    public async loadRunningChallenges(): Promise<ActiveChallengesWsObj> {
        try {
            let response = await this.cvApiService.get<ActiveChallengesWsObj>(`/api/v1/gamification/challenges/active?admin=true`);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to load active challenges.`);
        }
    }

    public async loadChallengesLogins(wallboardId: number, key: string): Promise<number[]> {
        try {
            let response = await this.cvApiService.post<number[]>(`/api/v1/challengesLogins/${wallboardId}`, JSON.stringify(key));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to load challenge logins.`);
        }
    }

    public async loadPointStoreProducts(slideKey: string, creatorId: number, productIds: number[] = null): Promise<any[]> {
        try {
            let response = await this.cvApiService.post<any[]>(`/api/v1/marketplaceproducts/${creatorId}`, JSON.stringify(
                {
                    moduleKey: slideKey,
                    productIDs: productIds
                }
            )); //PointStoreProduct[]
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to load point store products.`);
        }
    }

    public async loadModulePerformanceObjectives(metricId: number, creatorId: number, stateKey: string): Promise<any> {
        try {
            let response = await this.cvApiService.post<any>(`/api/v1/performanceObjective/${metricId}/${creatorId}`, JSON.stringify(stateKey)); //PerformanceObjectiveApiModel
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to load module performance objective.`);
        }
    }

    public async generateAccessGuid(): Promise<string> {
        try {
            let response = await this.cvApiService.get<string>(`/api/v1/accessGuid`);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to get access GUID.`);
        }
    }

    public async loadSalesPackagesByCompanyId(companyId: number): Promise<any[]> {
        try {
            let response = await this.cvApiService.get<any[]>(`/api/v1/companySalesPackages/${companyId}`); //SalesPackageAssignmentWsto[]
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to load sales packages for ${companyId}.`);
        }
    }

    public async loadAvailableDateRanges(wallboardId: number, moduleKey: string): Promise<any> {
        try {
            let response = await this.cvApiService.get<any>(`/api/v1/wallboards/availableDateRanges/${wallboardId}/${moduleKey}`);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to load available date ranges.`);
        }
    }   

    public async loadSlideoutSettings(wallboardId: number, moduleKey: string, slideId: number): Promise<any> {
        try {
            let response = await this.cvApiService.get<any>(`/api/v1/wallboards/slideoutSettings/${wallboardId}/${moduleKey}/${slideId}`);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to load slideout settings.`);
        }
    }

    public async saveSlideoutSettings(wallboardId: number, moduleKey: string, slideId: number, slideoutSettings): Promise<void> {
        try {
            let response = await this.cvApiService.put<void>(`/api/v1/wallboards/slideoutSettings/${wallboardId}/${slideId}`, JSON.stringify(slideoutSettings));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to save slide settings.`);
        }
    }

    /**
     * Returns the filename (with extention) of the racing theme preview image corresponding to the provided name.
     * @param themeName Name of the racing theme to get the filename of
     * @param bigPreview True gets the big preview image, false gets the small preview image; defaults to true
     */
    public racingThemePreviewImageFilename(themeName: string, bigPreview = true): string {
        switch (themeName) {
            case `Random`:
                return bigPreview ? `RandomRacingPreview.svg` : `RandomRacingSm.svg`;
            case `Horse Derby`:
                return bigPreview ? `racingHorsesPreview.jpg` : `HorseDerbySm.jpg`;
            case `Motocross`:
                return bigPreview ? `racingBikesPreview.jpg` : `MotocrossSm.jpg`;
            case `Rocket Race`:
                return bigPreview ? `racingRocketsPreview.png` : `RocketRaceSm.png`;
            case `Swim Meet`:
                return bigPreview ? `racingSwimmingPreview.jpg` : `SwimMeetSm.jpg`;
            case `ATV Racing`:
                return bigPreview ? `ATVRacingPreview.jpg` : `ATVRacingSm.jpg`;
            case `Boat Racing`:
                return bigPreview ? `BoatRacingPreview.jpg` : `BoatRacingSm.jpg`;
            case `Drag Racing`:
                return bigPreview ? `DragRacingPreview.jpg` : `DragRacingSm.jpg`;
            case `Formula One Racing`:
                return bigPreview ? `FormulaOneRacingPreview.jpg` : `FormulaOneRacingSm.jpg`;
            case `Jet Racing`:
                return bigPreview ? `JetRacingPreview.jpg` : `JetRacingSm.jpg`;
            case `Truck Racing`:
                return bigPreview ? `TruckRacingPreview.jpg` : `TruckRacingSm.jpg`;
            case `Raptor Racing`:
                return bigPreview ? `RaptorRacingPreview.jpg` : `RaptorRacingSm.jpg`;
            case `Snail Racing`:
                return bigPreview ? `SnailRacingPreview.jpg` : `SnailRacingSm.jpg`;
            case `Snowmobile Racing`:
                return bigPreview ? `SnowmobileRacingPreview.jpg` : `SnowmobileRacingSm.jpg`;
            case `Street Racing`:
                return bigPreview ? `StreetRacingPreview.jpg` : `StreetRacingSm.jpg`;
            case `Tortoise And Hare Racing`:
                return bigPreview ? `TortoiseRacingPreview.jpg` : `TortoiseRacingSm.jpg`;
            case `UFO Racing`:
                return bigPreview ? `UFORacingPreview.jpg` : `UFORacingSm.jpg`;
            case `Train Racing`:
                return bigPreview ? `TrainRacingPreview.jpg` : `TrainRacingSm.jpg`;
            case `Christmas Tree Racing`:
                return bigPreview ? `ChristmasTreeRacingPreview.jpg` : `ChristmasTreeRacingSm.jpg`;
            case `Cupid Racing`:
                return bigPreview ? `CupidRacersPreview.jpg` : `CupidRacersSm.jpg`;
            case `Easter Bunny Racing`:
                return bigPreview ? `EasterBunnyRacePreview.jpg` : `EasterBunnyRaceSm.jpg`;
            case `Elderly Racing`:
                return bigPreview ? `ElderlyRacingPreview.jpg` : `ElderlyRacingSm.jpg`;
            case `Fish Racing`:
                return bigPreview ? `FishRacingPreview.jpg` : `FishRacingSm.jpg`;
            case `Robot Racing`:
                return bigPreview ? `RobotRacingPreview.jpg` : `RobotRacingSm.jpg`;
            case `Sleigh Ride Racing`:
                return bigPreview ? `SleighRideRacingPreview.jpg` : `SleighrideRacingSm.jpg`;
            case `St Patricks Day Racing`:
                return bigPreview ? `StPatricksDayRacingPreview.jpg` : `StPatricksDayRacingSm.jpg`;
            case `Super Racing`:
                return bigPreview ? `SuperRacePreview.jpg` : `SuperRaceSm.jpg`;
            case `Turkey Racing`:
                return bigPreview ? `TurkeyRacingPreview.jpg` : `TurkeyRacingSm.jpg`;
            case `Zombie Racing`:
                return bigPreview ? `ZombieRacingPreview.jpg` : `ZombieRacingSm.jpg`;
            case `St Patricks Day Racing 2`:
                return bigPreview ? `StPatricksDayRacing2Preview.png` : `StPatricksDayRacing2Sm.png`;
            case `Thanksgiving Racing 1`:
                return bigPreview ? `ThanksgivingRacing1Preview.jpg` : `ThanksgivingRacing1Sm.jpg`;
            case `Thanksgiving Racing 2`:
                return bigPreview ? `ThanksgivingRacing2Preview.jpg` : `ThanksgivingRacing2Sm.jpg`;
            case `Easter Racing 2`:
                return bigPreview ? `EasterRacing2Preview.jpg` : `EasterRacing2Sm.jpg`;
            case `Valentine Racing 1`:
                return bigPreview ? `ValentineRacing1Preview.jpg` : `ValentineRacing1Sm.jpg`;
            case `Candy Land Racing`:
                return bigPreview ? `CandyLandRacingPreview.jpg` : `CandyLandRacingSm.jpg`;
            case `Christmas Carol Racing`:
                return bigPreview ? `ChristmasCarolRacingPreview.jpg` : `ChristmasCarolRacingSm.jpg`;
            case `Halloween Racing 1`:
                return bigPreview ? `HalloweenRacing1Preview.jpg` : `HalloweenRacing1Sm.jpg`;
            case `Jungle Racing 1`:
                return bigPreview ? `JungleRacing1Preview.jpg` : `JungleRacing1Sm.jpg`;
            case `Jungle Racing 2`:
                return bigPreview ? `JungleRacing2Preview.jpg` : `JungleRacing2Sm.jpg`;
            case `Mask Racing`:
                return bigPreview ? `MaskRacingPreview.jpg` : `MaskRacingSm.jpg`;
            case `Plant Racing`:
                return bigPreview ? `PlantRacingPreview.jpg` : `PlantRacingSm.jpg`;
            default:
                //return bigPreview ? `racingRocketsPreview.png` : `RocketRaceSm.png`;
                return "";
        }
    }
}
